import { useEffect, useState } from 'react'
import { Loader2, AlertCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import type { VersionAuditEntry } from '@/types/cgt'

const EVENT_LABELS: Record<VersionAuditEntry['eventType'], string> = {
  version_created: 'New version created',
  updated_in_place: 'Updated in place',
  form_ref_updated: 'Form reference updated',
  activated: 'Activated',
  inactivated: 'Inactivated',
  cascade_inactivated: 'Inactivated (cascade)',
}

function describe(entry: VersionAuditEntry): string {
  const oldV = entry.oldValue ?? {}
  const newV = entry.newValue ?? {}
  switch (entry.eventType) {
    case 'version_created':
      return oldV.version != null && newV.version != null
        ? `V${oldV.version} → V${newV.version}`
        : ''
    case 'updated_in_place': {
      const changes = Array.isArray(newV.changes) ? newV.changes.length : 0
      return changes ? `${changes} minor change${changes === 1 ? '' : 's'}` : ''
    }
    case 'form_ref_updated':
      return oldV.formVersion != null && newV.formVersion != null
        ? `${oldV.formName ?? 'Form'} V${oldV.formVersion} → V${newV.formVersion}`
        : ''
    default:
      return ''
  }
}

/**
 * Version-control history for one form or template family (86eybvqgn).
 * The loader is injected so the same dialog serves both entity types.
 */
export function VersionAuditDialog({
  open,
  onOpenChange,
  title,
  load,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  load: () => Promise<VersionAuditEntry[]>
}) {
  const [entries, setEntries] = useState<VersionAuditEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setEntries(null)
    setError(null)
    load()
      .then(setEntries)
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load version history.'))
    // `load` is recreated by parents per target — refetch when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, load])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Every version and status event is electronically signed and recorded.
          </DialogDescription>
        </DialogHeader>
        {error && (
          <div className='flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive'>
            <AlertCircle className='h-4 w-4 shrink-0' />
            {error}
          </div>
        )}
        {!error && entries === null && (
          <div className='flex items-center justify-center py-8'>
            <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
          </div>
        )}
        {entries !== null && entries.length === 0 && (
          <p className='py-6 text-center text-sm text-muted-foreground'>No version events yet.</p>
        )}
        {entries !== null && entries.length > 0 && (
          <div className='max-h-80 space-y-2 overflow-y-auto pr-1'>
            {entries.map(entry => (
              <div key={entry.id} className='rounded-lg border px-3 py-2 text-sm'>
                <div className='flex items-center justify-between gap-2'>
                  <span className='font-medium'>{EVENT_LABELS[entry.eventType]}</span>
                  <Badge variant='outline' className='text-[10px] text-muted-foreground'>
                    {new Date(entry.changedAt).toLocaleString()}
                  </Badge>
                </div>
                {describe(entry) && (
                  <p className='mt-0.5 text-xs text-muted-foreground'>{describe(entry)}</p>
                )}
                {entry.reason && (
                  <p className='mt-0.5 text-xs italic text-muted-foreground'>“{entry.reason}”</p>
                )}
                <p className='mt-0.5 text-xs text-muted-foreground'>
                  Signed by <span className='font-medium'>{entry.signedBy}</span>
                </p>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
